/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Heading, Text } from '@towersystems/towersystems-design-system';
import { map, get } from 'lodash';

/**
 * Internal dependencies
 */
import Card from 'components/card';
import AnnotatedSection from 'components/annotated-section';
import FormInputCheckbox from 'components/forms/form-checkbox';
import FormInputRadio from 'components/forms/form-radio';
import FormTextInput from 'components/forms/form-text-input';
import FormSelect from 'components/forms/form-select';
import FormNumberInput from 'components/forms/form-number-input';
import FormLabel from 'components/forms/form-label';

const ShopifyGraphQLPreferences = ({ saveConfigValue, 
                                     saveConfigValueInput, 
                                     hideResults, 
                                     values, 
                                     collectionSortOrders, 
                                     productTypes, 
                                     categoryOptions }) => {

    return (
        <Stack space='xlarge' dividers>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Product'
                    description='Choose your preferences when sending product data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='xlarge'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product title'
                                    onChange={saveConfigValue}
                                    name='updateProductName'
                                    checked={get(values, 'updateProductName', false)}
                                >
                                    When ticked the Description from Retailer will update Product Name/Title 
                                    in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product description'
                                    onChange={saveConfigValue}
                                    name='updateProductDescription'
                                    checked={get(values, 'updateProductDescription', false)}
                                >
                                    When ticked the Web Description from Retailer will be used to update 
                                    Description in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product tags'
                                    onChange={saveConfigValue}
                                    name='updateProductTags'
                                    checked={get(values, 'updateProductTags', false)}
                                >
                                    When ticked, the list of Product Tags from Retailer will be added to the Tags field in your ecommerce platform.  
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product vendor'
                                    onChange={saveConfigValue}
                                    name='updateProductVendor'
                                    checked={get(values, 'updateProductVendor', false)}
                                >
                                    Tags prefixed with Brand_ will be added to the Vendor field in Shopify. 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product weight'
                                    onChange={saveConfigValue}
                                    name='updateProductWeight'
                                    checked={get(values, 'updateProductWeight', false)}
                                >
                                    When ticked the product weight and unit field from Retailer will update the product weight 
                                    filed in your ecommerce platform. 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product price'
                                    onChange={saveConfigValue}
                                    name='updateProductPrice'
                                    checked={get(values, 'updateProductPrice', false)}
                                >
                                    When ticked the chosen price from Retailer (see inventory and pricing below) 
                                    will be used to update the product price in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product metafield with Classifications'
                                    onChange={saveConfigValue}
                                    name='updateMetafield'
                                    checked={get(values, 'updateMetafield', false)}
                                >
                                    When ticked the attribute from Retailer will be used to 
                                    update the product metafield in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product barcode'
                                    onChange={saveConfigValue}
                                    name='updateProductBarcode'
                                    checked={get(values, 'updateProductBarcode', false)}
                                >
                                    When ticked the Stock Number from Retailer will be used to 
                                    update the Barcode in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>

                            <Card.Section className="card__section-select">
                                <FormLabel>Sync Product Type With What Data?</FormLabel>
                                <Text size='xsmall' className="card__section-select___text">When a product is added what do you like the product type to be?</Text>
                                <FormSelect
                                    onChange={saveConfigValueInput} 
                                    name='defaultProductType'
                                    value={get(values, 'defaultProductType', '')}
                                >
                                    {map(productTypes, ({ value, name }) => (
                                        <option value={value} key={value}>
                                            {`${name}`}
                                        </option> 
                                    ))}
                                </FormSelect>
                            </Card.Section>

                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Collection'
                    description='Choose your preferences when sending collection data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section className="card__section-select">
                            <FormLabel>Default Collection Sort Order</FormLabel>
                            <Text size='xsmall' className="card__section-select___text">When a new collection is added what do you like the default sort order to be?</Text>
                            <FormSelect
                                onChange={saveConfigValueInput} 
                                name='defaultCollectionSortOrder'
                                value={get(values, 'defaultCollectionSortOrder', 'ALPHA_ASC')}
                            >
                                {map(collectionSortOrders, ({ value, name }) => (
                                    <option value={value} key={value}>
                                        {`${name}`}
                                    </option> 
                                ))}
                            </FormSelect>
                        </Card.Section>

                        <Card.Section>
                            <FormInputCheckbox
                                label='Update collection title'
                                onChange={saveConfigValue}
                                name='updateCollectionName'
                                checked={get(values, 'updateCollectionName', false)}
                            >
                                When ticked the Department/Category Name from Retailer will be used to update 
                                the corresponding Collection in your ecommerce platform.
                            </FormInputCheckbox>
                        </Card.Section>

                        <Card.Section className="card__section-select">
                            <FormLabel>Sync Category With What Data?</FormLabel>
                            <Text size='xsmall' className="card__section-select___text">When a product is added what do you like the category to be?</Text>
                            <FormSelect
                                onChange={saveConfigValueInput} 
                                name='defaultCategoryOption'
                                value={get(values, 'defaultCategoryOption', '')}
                            >
                                {map(categoryOptions, ({ value, name }) => (
                                    <option value={value} key={value}>
                                        {`${name}`}
                                    </option> 
                                ))}
                            </FormSelect>
                            {map(categoryOptions, ({ value, name, description }) => { 
                                return (value == get(values, 'defaultCategoryOption', '') && (
                           
                                    <div  className="card__section-select___text" dangerouslySetInnerHTML={{__html: description}}></div>
                                )) }       
                            )}  
                        </Card.Section>

                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>

            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Inventory & Pricing'
                    description='Choose your preferences when sending inventory and pricing data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='large'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product inventory levels'
                                    onChange={saveConfigValue}
                                    name='updateInventory'
                                    checked={get(values, 'updateInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be 
                                    used as the inventory level in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='TWC Track product inventory levels'
                                    onChange={saveConfigValue}
                                    name='trackInventory'
                                    checked={get(values, 'trackInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be subtracted 
                                    by the reported, but not yet imported, TWC sales 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Round down QOH To Whole Number'
                                    onChange={saveConfigValue}
                                    name='roundDownInventory'
                                    checked={get(values, 'roundDownInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be 
                                    rounded down in your ecommerce platform if they are in decimal
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use static quantity'
                                    onChange={saveConfigValue}
                                    name='useStaticQuantity'
                                    checked={get(values, 'useStaticQuantity', false)}
                                >
                                    When ticked, if the StaticQuantity received from Retailer is true, the inventory for the product will not be tracked in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Track Bundle Inventory'
                                    onChange={saveConfigValue}
                                    name='trackBundleInventory'
                                    checked={get(values, 'trackBundleInventory', true)}
                                >
                                    When ticked, the StaticQuantity received from Retailer will be ignored, the inventory for the bundle product will be tracked in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use web price'
                                    onChange={saveConfigValue}
                                    name='useWebPrice'
                                    checked={get(values, 'useWebPrice', false)}
                                >
                                    When ticked the Retailer Web Price will be used as the sell price of the item.  
                                    If unticked the system will use the Retailer Retail Price.  
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use catalogue price'
                                    onChange={saveConfigValue}
                                    name='useCataloguePrice'
                                    checked={get(values, 'useCataloguePrice', false)}
                                >
                                    When ticked, the Retailer Stock Item Catalogue price will be used in Shopify 
                                    as the Retail Price.  The regular price, either the web or retail price, will be set as the compare at price.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <Box className="card__section-outofstockqty">
                                    <FormLabel>Out of Stock Quantity:</FormLabel>
                                    <FormNumberInput name='outOfStockQty' onChange={saveConfigValueInput} value={get(values, 'outOfStockQty')} />
                                    <Text className="form-text-input__additional-content">The Quantity entered here will be used as 
                                    out of stock quantity. Any qty below this value will be marked as out of stock on your ecommerce platform.</Text>
                                </Box>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Unpublish out of stock'
                                    onChange={saveConfigValue}
                                    name='unpublishOutOfStock'
                                    checked={get(values, 'unpublishOutOfStock', false)}
                                >
                                    When ticked, products with chosen inventory will be unpublished or 
                                    unavailable for purchase on your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Unpublish out of stock products from Sales Channels'
                                    onChange={saveConfigValue}
                                    name='unpublishOutOfStockFromSalesChannels'
                                    checked={get(values, 'unpublishOutOfStockFromSalesChannels', false)}
                                >
                                    When ticked, products with out of stock quantity will be unpublished from all available Sales Channels.<br /><br />
                                    PLEASE NOTE: IN THE EVENT THAT ITEMS ARE RE-PUBLISHED ONLY THE ONLINE STORE CHANNEL WILL BE SELECTED.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>

            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Blog Posts'
                    description='Choose your preferences when sending blog posts data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section>
                            <FormInputCheckbox
                                label='Send Blog Posts'
                                onChange={saveConfigValue}
                                name='sendBlogPosts'
                                checked={get(values, 'sendBlogPosts', false)}
                            >
                                When ticked, Blog posts from Retailer will be sent to your ecommerce platform.
                            </FormInputCheckbox>
                        </Card.Section>

                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>

            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Throttle Backoff Time'
                    description='Choose the backoff time (in seconds) to pause before making additional API requests.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section>
                            <Box className="card__section-outofstockqty">
                                <FormLabel>Throttle Backoff Time (in seconds):</FormLabel>
                                <FormNumberInput name='throttleBackoffTime' onChange={saveConfigValueInput} min="1" value={get(values, 'throttleBackoffTime', 5)} />
                                <Text className="form-text-input__additional-content">If API has 200 Throttled error response is returned, the backoff time (in seconds) 
                    will be used to ensure enough time has passed before the API to retry the request.</Text>
                            </Box>
                        </Card.Section>

                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>
        </Stack>
    );
};

export default connect((state, ownProps) => ({}), {})(ShopifyGraphQLPreferences);
